import { createSelector } from "@reduxjs/toolkit";
import { companyTeamsAsMemberSelector } from "@store/members/members-selector";
import { canUseTeamsSelector } from "@store/sdb-company/sdb-company-selector";
import { teamAdapter } from "@store/teams/teams-slice";
import { canAddMembersToTeamManually, isAllMembersTeam } from "@utils/team-utils";

/** Kept as a constant so that the memoized selectors keep returning the same reference. */
const NO_TEAMS = [];

/**
 * Returns every team in the store, including the workspace role of each team.
 *
 * Unlike `teamsSelector` it ignores the page search text, so it should be used
 * whenever teams are looked up or offered as options (e.g. the members
 * autocomplete) instead of being listed on a searchable page.
 */
export const allTeamsSelector = createSelector(state => state.teams, companyTeamsAsMemberSelector, (teams, companyTeamsAsMember) => {
  const sdbTeams = teamAdapter.getSelectors().selectAll(teams).map(team => {
    const teamAsMember = companyTeamsAsMember.find(teamAsMember => teamAsMember.identity === team.id);
    return {
      ...team,
      role: teamAsMember?.role ?? "MEMBER"
    };
  });
  return sdbTeams.sort((teamA, teamB) => new Date(teamB.modifiedAt).getTime() - new Date(teamA.modifiedAt).getTime());
});

/**
 * Returns the teams that may still be assigned to a workspace, group or project.
 *
 * A workspace that lost the teams feature keeps its existing teams, but those teams must no
 * longer be offered as an option anywhere, so this list is empty for such a workspace.
 */
export const assignableTeamsSelector = createSelector(allTeamsSelector, canUseTeamsSelector, (teams, canUseTeams) => canUseTeams ? teams : NO_TEAMS);

/**
 * Returns the assignable teams that members can be added to by hand. Managed and auto-managed
 * teams, such as the all-members team or an SSO team, fill themselves and are left out.
 */
export const teamsAcceptingMembersSelector = createSelector(assignableTeamsSelector, teams => teams.filter(canAddMembersToTeamManually));

/**
 * Returns the teams to list on a page: those matching its search text, minus the all-members team,
 * which every workspace gets whatever its subscription is and which therefore only carries meaning
 * where the teams feature is available.
 */
function getListedTeams(teams, canUseTeams, searchText) {
  const searchTextLower = searchText.toLowerCase();
  return teams.filter(team => (canUseTeams || !isAllMembersTeam(team)) && team.name.toLowerCase().includes(searchTextLower));
}

/** Return the list of teams matching the current page search text. */
export const teamsSelector = createSelector(allTeamsSelector, canUseTeamsSelector, state => state.ui.search.debouncedSearchText, getListedTeams);

/**
 * Returns every team the selected member belongs to, including the workspace role of each team.
 *
 * Unlike `memberTeamsSelector` it ignores the page search text, so it should be used whenever the
 * teams of a member are looked up instead of being listed on a searchable page.
 */
export const allMemberTeamsSelector = createSelector(state => state.teams, companyTeamsAsMemberSelector, (teams, companyTeamsAsMember) => {
  const selectors = teamAdapter.getSelectors();
  return teams.memberTeams.ids.map(id => selectors.selectById(teams, id)).filter(team => Boolean(team)).map(team => {
    const teamAsMember = companyTeamsAsMember.find(teamAsMember => teamAsMember.identity === team.id);
    return {
      ...team,
      role: teamAsMember?.role ?? "MEMBER"
    };
  });
});

/**
 * Returns the list of teams the selected member belongs to, including the
 * workspace role of each team and filtered by the current search text.
 */
export const memberTeamsSelector = createSelector(allMemberTeamsSelector, canUseTeamsSelector, state => state.ui.search.debouncedSearchText, getListedTeams);

/**
 * Returns the fetching status of the member's teams.
 */
export const memberTeamsStatusSelector = createSelector(state => state.teams, teams => teams.memberTeams.status);

/** Returns the teams of each member that were looked up, by the identity of the member */
export const teamIdsByMemberSelector = createSelector(state => state.teams, teams => teams.teamIdsByMember);

/**
 * Gets a team entity by providing the team ID
 */
export function getTeamByIdSelector(teamId) {
  return createSelector(state => state.teams, teams => {
    if (!teamId) {
      return undefined;
    }
    return teamAdapter.getSelectors().selectById(teams, teamId);
  });
}

/**
 * Get the selected team using the selected team ID and finding it on the store,
 * returns null if there's no teamId or if the team could not be found in the store.
 */
export const selectedTeamIdSelector = createSelector(state => state.teams, teams => {
  const selectedTeamId = teams.selectedTeam.id;
  if (!selectedTeamId) {
    return null;
  }
  return teamAdapter.getSelectors().selectById(teams, selectedTeamId) ?? null;
});

/**
 * Get the selected team using the selected team ID and finding it on the store,
 * returns null if there's no teamId or if the team could not be found in the store,
 * including the user's role in the team if available.
 */
export const selectedTeamWithRoleSelector = createSelector(state => state.teams, companyTeamsAsMemberSelector, (teams, companyTeamsAsMember) => {
  const selectedTeamId = teams.selectedTeam.id;
  if (!selectedTeamId) {
    return null;
  }
  const team = teamAdapter.getSelectors().selectById(teams, selectedTeamId) ?? null;
  if (!team) {
    return null;
  }
  const teamAsMember = companyTeamsAsMember.find(teamAsMember => teamAsMember.identity === team.id);
  return {
    ...team,
    role: teamAsMember?.role ?? "MEMBER"
  };
});

/**
 * Get the team using the team ID and finding it on the store,
 * returns null if there's no teamId or if the team could not be found in the store,
 * including the user's role in the team if available.
 */
export function teamWithRoleByIdSelector(teamId) {
  return createSelector(state => state.teams, companyTeamsAsMemberSelector, (teams, companyTeamsAsMember) => {
    if (!teamId) {
      return null;
    }
    const team = teamAdapter.getSelectors().selectById(teams, teamId) ?? null;
    if (!team) {
      return null;
    }
    const teamAsMember = companyTeamsAsMember.find(teamAsMember => teamAsMember.identity === team.id);
    return {
      ...team,
      role: teamAsMember?.role ?? "MEMBER"
    };
  });
}

/**
 * Returns the fetching properties of the teams slice.
 */
export const fetchingTeamsFlagsSelector = createSelector(state => state.teams, teams => {
  return teams.fetching;
});

/**
 * Returns the members of the selected team.
 */
export const selectedTeamMembersSelector = createSelector(state => state.teams, teams => {
  return Object.values(teams.selectedTeam.members);
});

/** Returns the selected team as member type */
export const selectedTeamAsMemberSelector = createSelector(state => state.teams, teams => {
  return teams.selectedTeam.teamAsMember;
});

/** Returns the total count of members in the selected team */
export const selectedTeamTotalCountSelector = createSelector(state => state.teams, teams => {
  return teams.selectedTeam.totalCount;
});

/**
 * Returns the next team member ID if set.
 */
export const nextTeamMemberSelector = createSelector(state => state.teams, teams => {
  return teams.nextTeamMember;
});